import { useMutation, useQuery } from "@tanstack/react-query";

import { useCallback } from "react";
import useAxios from "../common/hooks/useAxios";
import {
  CREATE_SYSTEM_NOTICE,
  DELETE_SYSTEM_NOTICE,
  GET_SYSTEM_NOTICE,
  LIST_SYSTEM_NOTICES,
  LIST_SYSTEM_NOTICES_FOR_CUSTOMER,
  UPDATE_SYSTEM_NOTICE,
} from "../systemNotice/api/systemNotice-api";

export const SYSTEM_NOTICE_QUERIES = {
  listSystemNotices: "listSystemNotices",
  listSystemNoticesForCustomer: "listSystemNoticesForCustomer",
  getSystemNotice: "getSystemNotice",
  createSystemNotice: "createSystemNotice",
  deleteSystemNotice: "deleteSystemNotice",
};

const useSystemNoticeApi = (enabledApis = []) => {
  const axios = useAxios();

  const {
    data: systemNotices = [],
    isFetching: isSystemNoticesLoading,
    refetch: listSystemNotices,
  } = useQuery({
    queryKey: [SYSTEM_NOTICE_QUERIES.listSystemNotices],
    queryFn: () => axios.get(LIST_SYSTEM_NOTICES).then((res) => res.data),
    enabled: enabledApis.includes(SYSTEM_NOTICE_QUERIES.listSystemNotices),
    refetchOnMount: "always",
  });

  const {
    data: systemNoticesForCustomer = [],
    isFetching: isSystemNoticesForCustomerLoading,
    refetch: listSystemNoticesForCustomer,
  } = useQuery({
    queryKey: [SYSTEM_NOTICE_QUERIES.listSystemNoticesForCustomer],
    queryFn: () =>
      axios.get(LIST_SYSTEM_NOTICES_FOR_CUSTOMER).then((res) => res.data),
    enabled: enabledApis.includes(
      SYSTEM_NOTICE_QUERIES.listSystemNoticesForCustomer
    ),
  });

  const {
    mutateAsync: createSystemNotice,
    isPending: isCreateSystemNoticeLoading,
  } = useMutation({
    mutationFn: (data) =>
      axios
        .post(CREATE_SYSTEM_NOTICE, data, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        })
        .then((res) => res.data),
  });

  const { mutateAsync: updateSystemNotice, isPending: isUpdatingSystemNotice } =
    useMutation({
      mutationFn: (data) =>
        axios
          .put(UPDATE_SYSTEM_NOTICE, data, {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          })
          .then((res) => res.data),
    });

  const {
    mutateAsync: deleteSystemNotice,
    isPending: isDeleteSystemNoticeLoading,
  } = useMutation({
    mutationFn: (id) =>
      axios.delete(DELETE_SYSTEM_NOTICE(id)).then((res) => res.data),
  });

  const getSystemNoticeById = useCallback(
    async (id) => {
      return await axios.get(GET_SYSTEM_NOTICE(id)).then((res) => res.data);
    },
    [axios]
  );

  return {
    listSystemNoticesApi: {
      execute: listSystemNotices,
      isLoading: isSystemNoticesLoading,
      data: systemNotices,
    },
    listSystemNoticesForCustomerApi: {
      execute: listSystemNoticesForCustomer,
      isLoading: isSystemNoticesForCustomerLoading,
      data: systemNoticesForCustomer,
    },
    createSystemNoticeApi: {
      create: createSystemNotice,
      isLoading: isCreateSystemNoticeLoading,
    },
    updateSystemNoticeApi: {
      execute: updateSystemNotice,
      isLoading: isUpdatingSystemNotice,
    },
    deleteSystemNoticeApi: {
      delete: deleteSystemNotice,
      isLoading: isDeleteSystemNoticeLoading,
    },
    getSystemNoticeApi: {
      execute: getSystemNoticeById,
    },
  };
};

export default useSystemNoticeApi;
